/**
 * De tijden van het opslaan, op één plek en met een reden per getal.
 *
 * Opslaan is bij een CRDT geen aparte handeling: elke toetsaanslag reist als kleine
 * wijziging naar de pod, die het document gedebounced persisteert (ontwerp 6.3,
 * B5). Wat R-163 daarnaast vraagt blijft gelden en wordt hier geborgd: minstens elke
 * twee minuten doorschrijven, en bij het verlaten of wisselen van een scherm sturen
 * wat nog openstaat.
 */

/** Wachten tot het typen even stilstaat voordat er wordt weggeschreven. */
export const IDLE_MS = 400

/**
 * Pas na deze tijd verschijnt "bezig met opslaan".
 *
 * Een normale synchronisatie duurt minder dan een tiende seconde (valkuil 7 uit
 * bijlage C.4). Zonder deze vertraging flikkert de indicator bij elke aanslag, en
 * dan kijkt niemand er meer naar, ook niet als het een keer echt misgaat.
 */
export const DELAY_MS = 500

/** Hoe lang "bewaard" blijft staan voordat de indicator weer rust. */
export const SAVED_MS = 2500

/** De doorschrijfcadans die R-163 vraagt: minstens elke twee minuten. */
export const WRITE_THROUGH_MS = 120_000

/**
 * De vier standen van de indicator.
 *
 * `mislukt` is de enige die zich meteen meldt en blijft staan: een mislukte opslag is
 * het enige dat de invuller echt moet weten (R-80, R-141).
 */
export type StorageState = 'rustig' | 'bezig' | 'bewaard' | 'mislukt'

export const STORAGE_TEXT: Record<StorageState, { nl: string; en: string }> = {
  rustig: { nl: 'Alles is opgeslagen', en: 'Everything is saved' },
  bezig: { nl: 'Opslaan…', en: 'Saving…' },
  bewaard: { nl: 'Opgeslagen', en: 'Saved' },
  mislukt: {
    nl: 'Opslaan is niet gelukt. Uw werk staat nog in dit scherm; probeer het opnieuw.',
    en: 'Saving failed. Your work is still on this screen; please try again.',
  },
}

/**
 * Of deze stand aan een schermlezer wordt doorgegeven.
 *
 * R-141 vraagt een statusregio die wordt voorgelezen zonder de focus te verplaatsen,
 * kort, en afgeknepen zodat niet elke opslag iets zegt, terwijl een mislukte opslag
 * zich direct meldt. Dat betekent: `bezig` nooit voorlezen (die komt bij elke
 * aanslag terug) en `bewaard` alleen als er even niets is gebeurd.
 */
export function isAnnounced(state: StorageState): boolean {
  return state === 'mislukt' || state === 'bewaard'
}
